import React, { useState, useEffect, useRef, useMemo, KeyboardEvent } from "react";
import { Button, Input, InputProps, Radio, RadioProps, Icon, Image } from "semantic-ui-react";
import ModConfirmCustom from "./ModConfirmCustom";

const COLORS = {
  chat_light: "var(--primary-theme-chat-light)",
  grey: "rgb(204, 204, 204)",
  dark_grey: "#746A6A",
};

const IMAGES = {
  pencil_edit: "/static/images/pencil-edit-icon.png",
};

type CardCreateChatRoomProps = {
  // data
  userName: string;
  userLastName: string;
  userFirstName: string;
  userId: string;
  chatChannelId?: number;
  groupName?: string;
  nameAutoGen?: string;
  userList?: any[];
  memberList?: any[];
  // callback
  onEvent: any;
  onGetSearchUserList?: (search: string, selected: number[]) => void;
  onGetUserChatSubscription?: (params: any) => void;
  onClose?: () => void;
  onFinish?: (params: {
    userIds: number[];
    groupName: string;
    action: string;
    chatChannelId?: number;
    memberIds: number[];
  }) => void;
  onDelete?: (params: { ids: number[]; name?: string; isChangeName?: boolean }) => Promise<any>;
};

const getGroupName = ({
  members,
  userId,
  userName,
  userLastName,
  name,
  splitter = false,
}: {
  members: any[];
  userId: string;
  userName: string;
  userLastName: string;
  name?: string;
  splitter?: boolean;
}) => {
  const list = [...(members || [])].map((item) => ({
    ...item,
    ...item.user_obj,
  }));

  const getFirstName = (item: any) => {
    const cleanName = (item.last_name || "").replace(
      /(\.|\+|\*|\?|\^|\$|\(|\)|\[|\]|\{|\}|\||\\)/g,
      "\\$1"
    );
    const reg = new RegExp(cleanName, "g");
    return item?.full_name?.replace(reg, "")?.trim() || "";
  };

  const concatName = list.slice(0, 4).map(getFirstName).join(",");
  const number = `(${list.length})`;
  const firstName = getFirstName({
    full_name: userName,
    last_name: userLastName,
  });

  const friend = list.find((item) => item.id !== Number(userId));

  let title: any = { name: "", full_name: "", number: "" };

  switch (list.length - 1) {
    case 0:
      title = { name: "ห้องว่าง" };
      break;
    case 1:
      title = {
        name: friend?.full_name,
        full_name: `${userName}-${friend?.full_name}`,
      };
      break;
    case 2:
    case 3:
      title = {
        name: name ? name : `${concatName}`,
        number,
      };
      break;
    default:
      title = {
        name: name ? name : `${concatName},...`,
        number,
      };
  }

  if (name && splitter) {
    const reg = / \((\d+)\)$/g;
    const splitNumber = Number(reg.exec(name)?.[1]);
    splitNumber === list.length + 1 && (title.name = title.name.replace(reg, ""));
  }

  return title;
};

const getChangeName = (
  params: {
    members: any[];
    userId: string;
    userName: string;
    userLastName: string;
    userFirstName: string;
    name?: string | undefined;
    splitter?: boolean | undefined;
  },
  memberList: any[],
  groupName?: string,
  removeUser?: string
) => {
  let members = [...params.members].map((item) => ({
    ...item,
    ...item.user_obj,
  }));
  let filterMember = [...memberList];
  let newParams = { ...params };

  const groupNameString = (params: any) => {
    const title = getGroupName(params);
    return `${title.name} ${title.number || ""}`.trim();
  };

  if (!groupName || members.length === 1) {
    const name = groupNameString({ ...newParams, members: filterMember });
    return name;
  }
};

const DEFAULT_PROPS = {
  chatController: {},
  onEvent: () => null,
  userId: "",
  apiToken: "",
  divisionId: "",
  userName: "",
  userLastName: "",
  userFirstName: "",
};

const CardCreateChatRoom = React.memo<CardCreateChatRoomProps>((propsParam) => {
  const props = {
    ...DEFAULT_PROPS,
    ...Object.fromEntries(Object.entries(propsParam).filter(([_, value]) => value !== undefined)),
  };

  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [openModDelete, setOpenModDelete] = useState<any>(null);
  const [inputSearch, setInputSearch] = useState<string>("");
  const [memberList, setMemberList] = useState<any[]>([]);
  const [allMemberList, setAllMemberList] = useState<any[]>([]);
  const [groupName, setGroupName] = useState("");
  const [allUserList, setAllUserList] = useState<any[]>([]);
  const [nameAutoGen, setNameAutoGen] = useState("");

  const isMounted = useRef<boolean>(false);
  const membersRef = useRef<any[]>([]);
  const isConfirmRef = useRef<boolean>(false);
  const isDeleteRef = useRef<boolean>(false);

  useEffect(() => {
    getSearchUserList();

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      return;
    }

    const members = (props.memberList || []).map((item) => ({
      ...item,
      id: item.user_obj.id,
      full_name: item.user_obj.full_name,
      is_member: true,
    }));

    const allMembers = !!members.length
      ? members
      : [
          {
            id: Number(props.userId),
            full_name: props.userName,
            is_member: true,
          },
        ];
    const userList = members.filter((item) => item.id !== Number(props.userId));
    membersRef.current = props.memberList || [];

    setMemberList(userList);
    setAllMemberList(allMembers);
    setGroupName(props.groupName || "");
    setNameAutoGen(props.nameAutoGen || "");

    isMounted.current = true;
  }, [props.memberList, props.groupName, props.nameAutoGen]);

  useEffect(() => {
    const array = [...allUserList, ...(props.userList || [])];
    const key = "id";
    const arrayUniqueByKey = Array.from(new Map(array.map((item) => [item[key], item])).values());

    setAllUserList(arrayUniqueByKey);
  }, [props.userList]);

  useEffect(() => {
    if (isConfirmRef.current && !groupName) {
      handleConfirm();
      isConfirmRef.current = false;
    }
  }, [groupName]);

  useEffect(() => {
    if (isDeleteRef.current && !groupName) {
      handleConfirmDelete();
      isDeleteRef.current = false;
    }
  }, [groupName]);

  const userList = useMemo(() => {
    const memberIds = [{ id: Number(props.userId) }, ...memberList].map((item) => item.id);
    return (props.userList || []).filter((item) => !memberIds.includes(item.id));
  }, [props.userList, memberList]);

  const getSearchUserList = (search: string = "") => {
    props.onGetSearchUserList?.(search, checkedIds);
  };

  const handleChecked = (e: any, v: RadioProps) => {
    if (v.checked) {
      setCheckedIds([...checkedIds, Number(v.name)]);
    } else {
      setCheckedIds([...checkedIds.filter((id) => id !== Number(v.name))]);
    }
  };

  const handleStartChat = () => {
    if (!memberList.length && !props.chatChannelId) {
      return;
    }

    const oldIds = membersRef.current.map((item) => item.user_obj.id);

    props.onFinish?.({
      userIds: memberList.map((item) => item.id).filter((id) => !oldIds.includes(id)),
      groupName: groupName,
      action: "REQUEST",
      chatChannelId: props.chatChannelId,
      memberIds: memberList.map((item) => item.id),
    });
  };

  const handleConfirm = () => {
    const users = allUserList.filter((item) => checkedIds.includes(item.id));
    const members = [...allMemberList, ...users];

    setMemberList([...memberList, ...users]);
    setAllMemberList(members);
    handleSetGroupName(members);
    setCheckedIds([]);
  };

  const handleSetGroupName = (members: any[], removeUser = undefined) => {
    const name = getChangeName(
      { ...props, members: allMemberList },
      members,
      groupName,
      removeUser
    );

    if (typeof name === "string") {
      setNameAutoGen(name);
      return name;
    }
  };

  // Mod Delete
  const handleCloseModDelete = () => {
    setOpenModDelete(null);
  };

  const handleDelete = (item: any) => (e: any) => {
    setOpenModDelete(item);
  };

  const handleConfirmDelete = async () => {
    const members = allMemberList.filter((item) => item.id !== openModDelete?.id);

    if (members.length === 1 && !isDeleteRef.current && groupName) {
      isDeleteRef.current = true;
      return setGroupName("");
    }

    setMemberList(memberList.filter((item) => item.id !== openModDelete?.id));
    setAllMemberList(members);

    const name = handleSetGroupName(members, openModDelete?.id);

    if (openModDelete?.is_member) {
      await props.onDelete?.({
        ids: [openModDelete?.id],
        name: groupName,
        isChangeName:
          !!props.groupName && nameAutoGen === "ห้องว่าง" && !groupName && members.length === 1,
      });
    }

    handleCloseModDelete();
  };

  // Search
  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      getSearchUserList((e.target as any).value);
    }
  };

  const handleChangeInput = (e: any, v: InputProps) => {
    setInputSearch(v.value);
  };

  // Group name
  const handleChangeGroupName = (e: any, v: InputProps) => {
    const value = v.value;
    if (value === nameAutoGen) {
      setGroupName("");
    } else {
      setGroupName(value);
    }

    if (value === "") {
      setNameAutoGen("");
    }
  };

  return (
    <div className="CardCreate-chatRoom">
      {/* Header */}
      <div
        style={{
          backgroundColor: "#94c4f1",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          padding: "1rem 0.5rem 1rem 1rem",
        }}
      >
        <label>{"Create & Edit chat room"}</label>
        <Icon style={{ cursor: "pointer" }} name="close" onClick={props.onClose} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "47.5% 1fr" }}>
        {/* Choose member */}
        <div
          style={{
            padding: "20px 20px 25px 20px",
            borderRight: `1px solid ${COLORS.grey}`,
          }}
        >
          <label>Choose member for invite</label>
          <Input
            value={inputSearch}
            action={{
              icon: "search",
              onClick: () => getSearchUserList(inputSearch),
            }}
            placeholder="Search..."
            fluid
            style={{ width: "90%", marginTop: "1rem" }}
            onKeyPress={handleKeyPress}
            onChange={handleChangeInput}
          />
          <div style={{ color: COLORS.dark_grey, margin: "1.5rem 0px 0.75rem 0" }}>
            Selected ({checkedIds.length})
          </div>

          <div style={{ height: "53vh", overflow: "auto" }}>
            {userList.map((item) => (
              <div
                key={`user-list-${item?.id}`}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "13px 7.5% 13px 0",
                }}
              >
                <label>{item.full_name}</label>
                <Radio
                  name={item.id}
                  checked={checkedIds.includes(item.id)}
                  onClick={handleChecked}
                />
              </div>
            ))}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1.5rem",
            }}
          >
            <Button
              style={{
                backgroundColor:
                  (!!props.chatChannelId && allMemberList.length === 1) || !isMounted.current
                    ? "#767676"
                    : COLORS.chat_light,
                width: "fit-content",
              }}
              fluid
              disabled={(!!props.chatChannelId && allMemberList.length === 1) || !isMounted.current}
              onClick={handleConfirm}
            >
              Confirm Selected
            </Button>
          </div>
        </div>

        {/* Member */}
        <div>
          <div
            style={{
              height: "70px",
              backgroundColor: "rgba(198, 235, 243, 0.5)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "25px 15px 0px",
              }}
            >
              <Input
                value={groupName || nameAutoGen}
                ref={(instance: any) => {
                  instance?.inputRef?.current &&
                    (instance.inputRef.current.style.cssText = `background: transparent; border-color: transparent; font-size: 1.1rem; color: rgb(129 129 129); padding: 0;`);
                }}
                placeholder="Create group name"
                readOnly={memberList.length <= 1}
                style={{ width: "100%", marginRight: "0.75rem" }}
                onChange={handleChangeGroupName}
              />
              <Image src={IMAGES.pencil_edit} alt="pencil-edit.icon" style={{ width: "20px" }} />
            </div>
          </div>

          <div style={{ padding: "15px 20px 25px 20px" }}>
            <div style={{ color: COLORS.dark_grey, margin: "0.75rem 0 0.5rem 0" }}>
              Member ({memberList.length + 1})
            </div>

            <div style={{ height: "55vh", overflow: "auto" }}>
              {[{ id: null, full_name: props.userName }, ...memberList].map((item, index) => (
                <div
                  key={`user-list-${index}`}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "13px 7.5% 13px 0",
                  }}
                >
                  <label>{item.full_name}</label>
                  {index === 0 ? (
                    <div style={{ fontStyle: "italic", color: COLORS.grey }}>{"(you)"}</div>
                  ) : (
                    <Icon
                      name="close"
                      color="red"
                      style={{
                        fontSize: "0.75rem",
                        marginRight: "8px",
                        cursor: "pointer",
                      }}
                      onClick={handleDelete(item)}
                    />
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: "1.5rem",
              }}
            >
              <Button
                style={{
                  width: "fit-content",
                }}
                color={!!memberList.length || props.chatChannelId ? "green" : "grey"}
                fluid
                onClick={handleStartChat}
              >
                Start chat
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ModConfirmCustom
        open={!!openModDelete}
        onDeny={handleCloseModDelete}
        onApprove={handleConfirmDelete}
        approveText="Remove"
        denyText="Cancel"
        content={
          <div style={{ textAlign: "center", margin: "0 0 -1.25rem" }}>
            Remove {openModDelete?.full_name || ""} from this group?
          </div>
        }
      />
    </div>
  );
});

CardCreateChatRoom.displayName = "CardCreateChatRoom";

export default CardCreateChatRoom;
